import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useGetDocumentsByLoanApplication, useUploadDocument } from '../../../services/loanService';
import { UploadDocumentBodyDocumentType } from '../../../services/schemas/loanServiceSchemas/uploadDocumentBodyDocumentType';
import type { DocumentReference } from '../../../services/schemas/loanServiceSchemas/documentReference';
import { formatDocumentDate, getDocumentUploadDate } from '../../../utils/documentDates';

/** The vehicle's registration document (NaTIS): the same type the documents page and archive use. */
export const VEHICLE_REGISTRATION_TYPE = 'ASSET_OWNERSHIP_DOCUMENT';
/** Done when the vehicle is brought in after approval, so it can be uploaded later. */
export const CHECK_IN_FORM_TYPE = 'COLLATERAL_CHECK_IN_FORM';
export const VALUATION_TYPE = 'ASSET_VALUATION';
export const PROPERTY_TYPE = 'PROPERTY_DOCUMENT';

const UPLOAD_LABELS: Record<string, { description: string; done: string }> = {
  [VEHICLE_REGISTRATION_TYPE]: { description: 'Vehicle registration document (NaTIS)', done: 'Registration document uploaded' },
  [CHECK_IN_FORM_TYPE]: { description: 'Collateral check-in form', done: 'Check-in form uploaded' },
  [VALUATION_TYPE]: { description: 'Valuation report', done: 'Valuation report uploaded' },
  [PROPERTY_TYPE]: { description: 'Property ownership and security documents', done: 'Property documents uploaded' },
};

interface Props {
  applicationId: string;
  /** The collateral is a vehicle, so its registration document is needed before verifying. */
  registrationRequired: boolean;
  /** A vehicle shows NaTIS and the check-in form; other security shows the property documents. */
  isVehicle?: boolean;
  onRegistrationChange?: (uploaded: boolean) => void;
}

/**
 * The collateral's vehicle documents (finding #16). They are kept as the application's own documents,
 * not against one verification, so the check-in form can be added after approval and both show on the
 * documents page and in the archive.
 */
const CollateralVehicleDocuments: React.FC<Props> = ({ applicationId, registrationRequired, isVehicle = true, onRegistrationChange }) => {
  const { data: documents, refetch } = useGetDocumentsByLoanApplication(applicationId, {}, { query: { enabled: !!applicationId } });
  const upload = useUploadDocument();
  const [busy, setBusy] = useState<string | null>(null);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const current = (type: string): DocumentReference | undefined =>
    (documents || []).find(d => d.documentType === type && d.status !== 'SUPERSEDED');

  const hasRegistration = !!current(VEHICLE_REGISTRATION_TYPE);
  useEffect(() => { onRegistrationChange?.(hasRegistration); }, [hasRegistration, onRegistrationChange]);

  const send = async (type: string, file: File) => {
    setBusy(type);
    try {
      await upload.mutateAsync({
        loanApplicationId: applicationId,
        data: {
          file,
          documentType: type as UploadDocumentBodyDocumentType,
          description: UPLOAD_LABELS[type]?.description || 'Security document',
        },
      });
      toast.success(UPLOAD_LABELS[type]?.done || 'Document uploaded');
      await refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The document could not be uploaded');
    } finally {
      setBusy(null);
      if (inputs.current[type]) inputs.current[type]!.value = '';
    }
  };

  const row = (type: string, title: string, description: string, required: boolean) => {
    const doc = current(type);
    const missing = required && !doc;
    return (
      <div className="col-md-6" key={type}>
        <div className={`card mb-3 ${doc ? 'border-success' : missing ? 'border-danger' : ''}`}>
          <div className="card-body">
            <h6 className="mb-1">{title} {required && <span className="text-danger">*</span>}</h6>
            <p className="small text-muted mb-2">{description}</p>
            {doc ? (
              <p className="small text-success mb-2">
                <i className="bi bi-check-circle me-1"></i>{doc.name || 'Uploaded'}
                {getDocumentUploadDate(doc) ? ` · ${formatDocumentDate(getDocumentUploadDate(doc))}` : ''}
              </p>
            ) : missing ? (
              <p className="small text-danger mb-2">Needed before this collateral can be verified</p>
            ) : null}
            <input type="file" className="d-none" accept=".pdf,.jpg,.jpeg,.png" id={`vehicle-doc-${type}`}
              ref={el => { inputs.current[type] = el; }}
              onChange={e => { const f = e.target.files?.[0]; if (f) send(type, f); }} disabled={!!busy} />
            <label htmlFor={`vehicle-doc-${type}`} className={`btn btn-sm ${doc ? 'btn-outline-success' : 'btn-outline-primary'} ${busy ? 'disabled' : ''}`}>
              {busy === type
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Uploading…</>
                : <><i className={`bi ${doc ? 'bi-arrow-repeat' : 'bi-upload'} me-2`}></i>{doc ? 'Replace' : 'Upload'}</>}
            </label>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4">
      <h6 className="fw-semibold mb-2">
        <i className={`bi ${isVehicle ? 'bi-car-front' : 'bi-house'} me-2 text-primary`}></i>{isVehicle ? 'Vehicle documents' : 'Security documents'}
      </h6>
      <div className="row">
        {isVehicle
          ? row(VEHICLE_REGISTRATION_TYPE, 'Vehicle Registration Document', 'The NaTIS registration certificate showing the registered owner.', registrationRequired)
          : row(PROPERTY_TYPE, 'Property Ownership & Security Documents', 'Title deed or bond documents, where property is the security.', false)}
        {row(VALUATION_TYPE, 'Valuation Report', 'Independent valuation of the security.', false)}
        {isVehicle && row(CHECK_IN_FORM_TYPE, 'Check-in Form',
          'Completed when the vehicle is brought to the office after approval. Optional now; it can be uploaded here or on the documents page later.', false)}
      </div>
    </div>
  );
};

export default CollateralVehicleDocuments;
